import React from 'react';
import { Link } from 'react-router-dom';
import { TrashIcon, PlusIcon, MinusIcon, BoltIcon } from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useEcommerceTracking } from '../hooks/useAnalytics';

const CartPage = () => {
  const { 
    items, 
    removeFromCart, 
    updateQuantity, 
    clearCart,
    getCartSummary
  } = useCart();
  const { user } = useAuth();
  const { trackRemoveFromCart } = useEcommerceTracking();

  const { subtotal, tax, shipping, total, itemCount } = getCartSummary();

  // Handle remove from cart with analytics tracking
  const handleRemoveFromCart = (itemId) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      // Only track analytics for regular products, not bundles
      if (item.product && !item.is_bundle) {
        trackRemoveFromCart(item.product, item.quantity);
      }
    }
    removeFromCart(itemId);
  };

  // Handle quantity update with analytics tracking
  const handleUpdateQuantity = (itemId, newQuantity) => {
    const item = items.find(i => i.id === itemId);
    if (item && newQuantity > 0) {
      // Only track analytics for regular products, not bundles
      if (item.product && !item.is_bundle) {
        // Track remove if quantity decreased
        if (newQuantity < item.quantity) {
          const removedQuantity = item.quantity - newQuantity;
          trackRemoveFromCart(item.product, removedQuantity);
        }
        // Track add if quantity increased
        else if (newQuantity > item.quantity) {
          const addedQuantity = newQuantity - item.quantity;
          // We would need trackAddToCartEvent here, but it's not imported
          // For now, we'll just update the quantity
        }
      }
    }
    updateQuantity(itemId, newQuantity);
  };

  // Helper function to get the correct price for display
  const getItemPrice = (product) => {
    if (!product) {
      console.warn('getItemPrice: product is null/undefined');
      return 0;
    }
    
    let price = 0;
    if (user?.role === 'company_admin' || user?.role === 'company_user') {
      price = parseFloat(product.price_b2b) || parseFloat(product.price_b2c) || 0;
    } else {
      price = parseFloat(product.price_b2c) || 0;
    }
    
    if (isNaN(price)) {
      console.warn('getItemPrice: price is NaN for product:', product.name, 'price_b2c:', product.price_b2c, 'price_b2b:', product.price_b2b);
      return 0;
    }
    
    return price;
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">עגלת הקניות שלך</h1>
            <p className="text-gray-600 mb-8">העגלה שלך ריקה כרגע</p>
            <Link
              to="/products"
              className="btn btn-primary"
            >
              התחל לקנות
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-safe">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 lg:py-16 pb-24 sm:pb-12">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-8">עגלת קניות ({itemCount})</h1>

        {/* Mobile-First: Stack on mobile, grid on desktop */}
        <div className="space-y-6 lg:grid lg:grid-cols-12 lg:gap-x-8 lg:space-y-0">
          {/* Cart Items - Mobile-First */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-lg shadow">
              <div className="px-3 py-4 sm:px-4 sm:py-6 lg:px-6">
                <div className="flow-root">
                  <ul className="-my-4 sm:-my-6 divide-y divide-gray-200">
                    {items.map((item) => {
                      // Check if this is a bundle or a regular product
                      const isBundle = item.is_bundle || item.bundle;
                      const itemData = isBundle ? item.bundle : item.product;
                      
                      // Safety check - skip if itemData is null
                      if (!itemData) {
                        console.warn('Item data is null:', item);
                        return null;
                      }
                      
                      const itemName = isBundle ? itemData.title : itemData.name;
                      const itemImage = itemData.images?.[0] || itemData.bundle_image;
                      const itemLink = isBundle ? `/bundles/${itemData.id}` : `/products/${itemData.id}`;
                      const itemPrice = isBundle ? parseFloat(itemData.bundle_price) : getItemPrice(itemData);
                      const itemSku = isBundle ? itemData.bundle_sku : itemData.sku;
                      
                      return (
                      <li key={item.id} className="py-4 sm:py-6 flex gap-3 sm:gap-4">
                        {/* Product Image - Mobile optimized size */}
                        <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 border border-gray-200 rounded-md overflow-hidden">
                          <img
                            src={itemImage ? (itemImage.startsWith('http') ? `${itemImage}?id=${itemData.id}` : `/api${itemImage}?id=${itemData.id}`) : 'https://picsum.photos/400/400'}
                            alt={itemName}
                            className="w-full h-full object-center object-cover"
                          />
                        </div>

                        <div className="flex-1 flex flex-col min-w-0">
                          <div>
                            <div className="flex justify-between text-sm sm:text-base font-medium text-gray-900">
                              <h3 className="flex-1 min-w-0">
                                <Link to={itemLink} className="line-clamp-2">
                                  {itemName}
                                  {isBundle && <span className="text-xs text-primary-600 mr-2">חבילה</span>}
                                </Link>
                              </h3>
                              <p className="mr-3 sm:mr-4 flex-shrink-0">
                                ₪{(itemPrice * item.quantity).toFixed(2)}
                                {isBundle && item.bundle_discount > 0 && (
                                  <span className="text-xs text-green-600 block">
                                    חסכת ₪{(item.bundle_discount * item.quantity).toFixed(2)}
                                  </span>
                                )}
                              </p>
                            </div>
                            {itemSku && (
                              <p className="mt-1 text-sm text-gray-500">
                                SKU: {itemSku}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 mt-1">
                              ₪{itemPrice.toFixed(2)} × {item.quantity}
                            </p>
                          </div>
                          
                          <div className="flex-1 flex items-end justify-between text-xs sm:text-sm">
                            {/* Quantity controls - Mobile optimized touch targets */}
                            <div className="flex items-center border border-gray-200 rounded-lg">
                              <button
                                type="button"
                                onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-50 touch-manipulation"
                                aria-label="Decrease quantity"
                              >
                                <MinusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                              <span className="mx-2 sm:mx-3 text-gray-900 font-medium min-w-[2rem] text-center">
                                {item.quantity}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-50 touch-manipulation"
                                aria-label="Increase quantity"
                              >
                                <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleRemoveFromCart(item.id)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-500 p-2 touch-manipulation"
                            >
                              <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                              <span className="hidden sm:inline">הסר</span>
                            </button>
                          </div>
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
              
              {/* Cart actions */}
              <div className="border-t border-gray-200 px-4 py-6 sm:px-6">
                <div className="flex justify-between">
                  <Link
                    to="/products"
                    className="btn btn-outline"
                  >
                    המשך לקנות
                  </Link>
                  <button
                    onClick={clearCart}
                    className="text-red-600 hover:text-red-500 text-sm font-medium"
                  >
                    נקה עגלה
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary - Sticky on desktop, normal flow on mobile */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-lg shadow lg:sticky lg:top-4">
              <div className="px-4 py-6 sm:px-6">
                <h2 className="text-lg font-medium text-gray-900">סיכום הזמנה</h2>

                <div className="mt-6 space-y-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">סכום ביניים</span>
                    <span>₪{subtotal.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">מע"מ (18%)</span>
                    <span>₪{tax.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">משלוח</span>
                    <span>
                      {shipping === 0 ? (
                        <span className="text-green-600">חינם</span>
                      ) : (
                        `₪${shipping.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  
                  {shipping > 0 && (
                    <p className="text-xs text-gray-500">
                      משלוח חינם על הזמנות מעל ₪300
                    </p>
                  )}
                  
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between text-base font-medium text-gray-900">
                      <span>סה"כ</span>
                      <span>₪{total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Desktop Checkout Buttons */}
                <div className="hidden sm:block mt-6 space-y-3">
                  {user?.role === 'b2b' ? (
                    <Link
                      to="/fast-checkout"
                      className="btn btn-primary w-full justify-center flex items-center"
                    >
                      <BoltIcon className="h-5 w-5 ml-2" />
                      תשלום מהיר B2B
                    </Link>
                  ) : (
                    <Link
                      to="/checkout"
                      className="btn btn-primary w-full justify-center"
                    >
                      מעבר לתשלום
                    </Link>
                  )}
                </div>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-500">
                    תשלום מאובטח עם הצפנה 256-bit SSL
                  </p>
                </div>

                {/* Shipping info */}
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    מידע על משלוח
                  </h3>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• משלוח חינם על הזמנות מעל ₪300</li>
                    <li>• זמן אספקה: 3-5 ימי עסקים</li>
                    <li>• אפשרות איסוף עצמי</li>
                    <li>• מדיניות החזרה: 30 יום</li>
                  </ul>
                </div>

                {/* Security badges */}
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <div className="flex justify-center space-x-4 space-x-reverse">
                    <div className="text-xs text-gray-500 text-center">
                      <div className="w-12 h-8 bg-gray-200 rounded mb-1"></div>
                      SSL מאובטח
                    </div>
                    <div className="text-xs text-gray-500 text-center">
                      <div className="w-12 h-8 bg-gray-200 rounded mb-1"></div>
                      Visa/MC
                    </div>
                    <div className="text-xs text-gray-500 text-center">
                      <div className="w-12 h-8 bg-gray-200 rounded mb-1"></div>
                      PayPal
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Checkout Bar - Only on Mobile */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-2xl z-[60]">
        <div className="max-w-7xl mx-auto space-y-3">
          {/* Total Summary */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">סה"כ לתשלום:</span>
            <span className="text-xl font-bold text-gray-900">₪{total.toFixed(2)}</span>
          </div>
          
          {/* Checkout Button */}
          {user?.role === 'b2b' ? (
            <Link
              to="/fast-checkout"
              className="btn btn-primary w-full justify-center flex items-center py-3.5 text-base font-semibold touch-manipulation"
            >
              <BoltIcon className="h-5 w-5 ml-2" />
              תשלום מהיר B2B
            </Link>
          ) : (
            <Link
              to="/checkout"
              className="btn btn-primary w-full justify-center py-3.5 text-base font-semibold touch-manipulation"
            >
              מעבר לתשלום
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartPage;
